import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/pricing";
import { TrendingUp, Wallet, Target } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Rep/OM/Admin commission forecaster: shows locked-in, at-stake, projected
 * for the current calendar month.
 */
export function ForecastPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/commissions/forecast");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="mt-6 border border-line rounded-sm bg-bg-surface p-5 animate-pulse" data-testid="forecast-panel-loading">
        <div className="h-24 bg-bg rounded-sm" />
      </div>
    );
  }
  if (!data) return null;

  const target = data.target_cents || 0;
  const projected = data.projected_cents || 0;
  const pct = target > 0 ? Math.min(100, Math.round((projected / target) * 100)) : 0;
  const hit = data.target_hit;

  return (
    <section
      className="mt-6 border border-line rounded-sm bg-bg-surface overflow-hidden"
      data-testid="forecast-panel"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <Wallet size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">Paycheck forecaster</h2>
        <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          this month
        </span>
      </div>

      <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-line">
        <ForecastTile
          label="Locked in"
          hint="Paid + closed this month"
          value={data.locked_in_cents}
          testId="forecast-locked-in"
          accent="#22D3C0"
        />
        <ForecastTile
          label="At stake"
          hint={`${data.at_stake_count} open proposal${data.at_stake_count === 1 ? "" : "s"}`}
          value={data.at_stake_cents}
          testId="forecast-at-stake"
          accent="#F4B860"
        />
        <ForecastTile
          label="Projected"
          hint={`If everything closes`}
          value={data.projected_cents}
          testId="forecast-projected"
          accent="#FF6A00"
          bold
        />
      </div>

      <div className="px-5 py-4 border-t border-line">
        <div className="flex items-baseline justify-between mb-2">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-ink-muted">
            <Target size={11} />
            Target
            <span className="text-ink tabular-nums">{fmtMoney(target)}</span>
          </div>
          <div
            className={`text-[11px] font-mono uppercase tracking-widest tabular-nums ${
              hit ? "text-cyan" : "text-ink-secondary"
            }`}
            data-testid="forecast-pct"
          >
            {pct}%
          </div>
        </div>
        <div className="h-2 bg-bg rounded-sm overflow-hidden border border-line">
          <div
            className="h-full transition-all"
            style={{
              width: `${pct}%`,
              background: hit
                ? "linear-gradient(90deg, #22D3C0 0%, #4ADE80 100%)"
                : "linear-gradient(90deg, #F4B860 0%, #FF6A00 100%)",
              boxShadow: hit ? "0 0 10px rgba(34, 211, 192, 0.6)" : "none",
            }}
          />
        </div>
      </div>

      {data.at_stake_rows && data.at_stake_rows.length > 0 && (
        <div className="px-5 pb-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2 mt-2">
            Open proposals worth chasing
          </div>
          <div className="divide-y divide-line" data-testid="forecast-at-stake-list">
            {data.at_stake_rows.slice(0, 5).map((r, i) => (
              <div key={i} className="py-2 flex items-center gap-3 text-sm" data-testid={`forecast-row-${i}`}>
                <TrendingUp size={12} className="text-ink-muted shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-ink truncate">{r.lead_name || "Unnamed lead"}</div>
                  <div className="text-[11px] text-ink-muted">
                    {r.lead_city || ""} · {r.status}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-ink tabular-nums font-mono">
                    {fmtMoney(r.your_cut_cents)}
                  </div>
                  <div className="text-[10px] text-ink-muted font-mono">your cut</div>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/commissions"
            className="mt-2 inline-block text-[11px] text-cyan hover:text-cyan-hover transition-colors"
            data-testid="forecast-see-all"
          >
            See full commissions →
          </Link>
        </div>
      )}
    </section>
  );
}

function ForecastTile({ label, hint, value, testId, accent, bold }) {
  return (
    <div className="px-5 py-4" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
        {label}
      </div>
      <div
        className={`mt-1 ${bold ? "text-3xl font-bold" : "text-2xl font-semibold"} tabular-nums`}
        style={{ color: accent }}
      >
        {fmtMoney(value)}
      </div>
      <div className="text-[11px] text-ink-muted mt-1">{hint}</div>
    </div>
  );
}
